import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { CircularProgress } from '@mui/material';
import {
  Checkbox,
  Chip,
  Modal,
  Radio,
  theme as designSystemTheme,
  Tooltip,
} from '@sb/cc-design-system';
import { isEmpty } from 'lodash';
import { KeyboardEvent, useEffect, useMemo, useState } from 'react';
import DesignSystemThemeProvider from 'src/components/designSystem/DesignSystemThemeProvider';
import { NoResultAlert } from 'src/components/noResultAlert';
import { Pagination } from 'src/components/pagination/Pagination';
import {
  useGetCCAssets,
  useGetPaginatedProductsWithVariations,
  useGetProductVariationFlavors,
  useGetProductVariationTypes,
} from 'src/hooks';
import { IProduct } from 'src/interfaces';
import { lang } from 'src/utils';

import {
  getMatchingVariationName,
  getProductDisplayName,
  getProductListQueryFilters,
  getProductsWithAssets,
  getProductsWithSizeAndFlavor,
  getVariationLabel,
  PRODUCT_MODAL_PAGE_SIZE,
  SEARCH_TYPES,
  SearchTermsByType,
  SearchType,
  searchTypeOptions,
} from './ProductListModalV2.helper';
import { ProductListModalV2Props } from './ProductListModalV2.interface';
import {
  LoadingWrapper,
  modalDialogProps,
  modalFooterStyle,
  modalHeaderStyle,
  NoResultsWrapper,
  ProductListModalContent,
  ProductNameContent,
  ProductNameText,
  ProductNameWrapper,
  ProductRow,
  ProductRows,
  ProductTable,
  ProductTableHead,
  ProductTableHeadingText,
  SearchFilterChips,
  VariantChipsWrapper,
  VariantCount,
  VariantCountWrapper,
} from './ProductListModalV2.styled';

export const ProductListModalV2 = (props: ProductListModalV2Props) => {
  const [searchType, setSearchType] = useState<SearchType>(
    SEARCH_TYPES.productCode,
  );
  const [typedSearchValue, setTypedSearchValue] = useState('');
  const [searchTermsByType, setSearchTermsByType] = useState<SearchTermsByType>(
    {},
  );
  const [isExactProductNameSearch, setIsExactProductNameSearch] =
    useState(false);
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<IProduct['product_code'][]>(
    [],
  );
  const [selectedProductsMap, setSelectedProductsMap] = useState<
    Map<IProduct['product_code'], IProduct>
  >(new Map());

  const { data: assets = [] } = useGetCCAssets();
  const { data: productVariationTypes = [] } = useGetProductVariationTypes([
    'sort=*name',
  ]);
  const { data: productVariationFlavors = [] } = useGetProductVariationFlavors([
    'sort=*name',
  ]);

  useEffect(() => {
    setPage(1);
  }, [searchTermsByType, isExactProductNameSearch, props.filters]);

  useEffect(() => {
    if (!props.open) {
      setSelectedIds([]);
      setSelectedProductsMap(new Map());
    }
  }, [props.open]);

  const queryFilters = useMemo(() => {
    return getProductListQueryFilters({
      filters: props.filters,
      isExactProductNameSearch,
      searchTermsByType,
    });
  }, [searchTermsByType, isExactProductNameSearch, props.filters]);

  const {
    data: productsData,
    isLoading,
    isFetching,
  } = useGetPaginatedProductsWithVariations(page, queryFilters, {
    keepPreviousData: true,
  });

  const allProducts = getProductsWithSizeAndFlavor(
    productsData?.data ?? [],
    productVariationTypes,
    productVariationFlavors,
  );
  const totalPages =
    productsData?.totalPages ||
    Math.ceil((productsData?.totalCount ?? 0) / PRODUCT_MODAL_PAGE_SIZE);
  const hasResult = allProducts.length > 0;
  const isLoadingProducts = isLoading || isFetching;
  const isSelectDisabled = isEmpty(selectedIds) || isLoading;
  const searchTermChips = Object.entries(searchTermsByType) as [
    SearchType,
    string,
  ][];
  const isExactMatchDisabled = searchType !== SEARCH_TYPES.productName;

  const selectedProducts = useMemo(() => {
    return selectedIds
      .map((id) => selectedProductsMap.get(id))
      .filter((product): product is IProduct => product !== undefined);
  }, [selectedIds, selectedProductsMap]);

  const toggleProductSelection = (product: IProduct) => {
    setSelectedIds((prevSelectedIds) => {
      if (props.singleSelectionMode) {
        setSelectedProductsMap(new Map([[product.product_code, product]]));

        return [product.product_code];
      }

      const isSelected = prevSelectedIds.includes(product.product_code);

      setSelectedProductsMap((prevMap) => {
        const nextMap = new Map(prevMap);

        if (isSelected) {
          nextMap.delete(product.product_code);
        } else {
          nextMap.set(product.product_code, product);
        }

        return nextMap;
      });

      return isSelected
        ? prevSelectedIds.filter((id) => id !== product.product_code)
        : [...prevSelectedIds, product.product_code];
    });
  };

  const handleSelect = () => {
    props.onSelect(getProductsWithAssets(selectedProducts, assets));
    props.onClose();
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    nextPage: number,
  ) => {
    setPage(nextPage);
  };

  const handleSearch = (value: string) => {
    const nextSubmittedSearchValue = value.trim();

    setSearchTermsByType((prevSearchTerms) => {
      const nextSearchTerms = { ...prevSearchTerms };

      if (isEmpty(nextSubmittedSearchValue)) {
        delete nextSearchTerms[searchType];
      } else {
        nextSearchTerms[searchType] = nextSubmittedSearchValue;
      }

      return nextSearchTerms;
    });

    setTypedSearchValue('');
  };

  const handleRemoveSearchTerm = (searchTypeToRemove: SearchType) => {
    setSearchTermsByType((prevSearchTerms) => {
      const nextSearchTerms = { ...prevSearchTerms };
      delete nextSearchTerms[searchTypeToRemove];

      return nextSearchTerms;
    });

    if (searchType === searchTypeToRemove) {
      setTypedSearchValue('');
    }
  };

  const handleSearchTypeChange = (value: string) => {
    const nextSearchType = value as SearchType;

    setSearchType(nextSearchType);
    setTypedSearchValue('');
  };

  const renderProductRow = (product: IProduct) => {
    const visibleVariations = product?.productVariations ?? [];
    const matchedVariationName =
      !product.name_override &&
      searchTermsByType[SEARCH_TYPES.productVariation] &&
      searchType === SEARCH_TYPES.productVariation
        ? getMatchingVariationName(
            product,
            searchTermsByType[SEARCH_TYPES.productVariation],
          )
        : undefined;
    const productName =
      matchedVariationName ?? getProductDisplayName(product, assets);
    const productNameLabel = `${product.product_code} - ${productName}`;
    const variantCountLabel = `${visibleVariations.length} ${
      visibleVariations.length > 1 ? `${lang.variant}s` : lang.variant
    }`;

    const checked = selectedIds.includes(product.product_code);

    return (
      <ProductRow
        key={product.product_code}
        $selected={checked}
        role="button"
        tabIndex={0}
        onClick={() => toggleProductSelection(product)}
        onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggleProductSelection(product);
          }
        }}>
        <ProductNameWrapper>
          {props.singleSelectionMode ? (
            <Radio
              data-testid={`product-list-modal-radio-${product.product_code}`}
              disableRipple
              value={product.product_code}
              checked={checked}
              onClick={(event) => event.stopPropagation()}
              onChange={() => toggleProductSelection(product)}
            />
          ) : (
            <Checkbox
              data-testid={`product-list-modal-checkbox-${product.product_code}`}
              checked={checked}
              onClick={(event) => event.stopPropagation()}
              onChange={() => toggleProductSelection(product)}
            />
          )}
          <ProductNameContent>
            <Tooltip
              title={productNameLabel}
              placement="top"
              showOnOverflowOnly>
              <ProductNameText variant="body2">
                {productNameLabel}
              </ProductNameText>
            </Tooltip>
            <VariantCountWrapper>
              <VariantCount variant="caption">{variantCountLabel}</VariantCount>
            </VariantCountWrapper>
          </ProductNameContent>
        </ProductNameWrapper>

        <VariantChipsWrapper>
          {visibleVariations.map((variation) => (
            <Chip
              key={variation.product_code}
              label={getVariationLabel(
                variation,
                productVariationTypes,
                productVariationFlavors,
              )}
              borderColor={designSystemTheme.palette.primary.main}
              borderWidth={1}
              chipBackgroundColor={designSystemTheme.palette.common.white}
              textColor={designSystemTheme.palette.primary.main}
              maxWidth={160}
              tooltipProps={{ placement: 'top' }}
            />
          ))}
        </VariantChipsWrapper>
      </ProductRow>
    );
  };

  return (
    <DesignSystemThemeProvider>
      <Modal
        open={props.open}
        onClose={props.onClose}
        title={props.heading}
        width={1024}
        showHeaderDivider
        hideCloseIcon
        showConfirmButton
        showCancelButton
        cancelButtonText={lang.cancel}
        confirmButtonText={lang.select}
        onCancelAction={props.onClose}
        onConfirmAction={handleSelect}
        confirmButtonProps={{
          disabled: isSelectDisabled,
        }}
        dialogProps={modalDialogProps}
        searchValue={typedSearchValue}
        searchPlaceholder="Search..."
        onSearchChange={setTypedSearchValue}
        onSearch={handleSearch}
        searchTypeOptions={searchTypeOptions}
        searchTypeValue={searchType}
        onSearchTypeChange={(value) => handleSearchTypeChange(value as string)}
        showExactMatchToggle
        exactMatch={isExactProductNameSearch}
        exactMatchLabel={lang.exactMatch}
        exactMatchDisabled={isExactMatchDisabled}
        onExactMatchChange={setIsExactProductNameSearch}
        headerStyle={modalHeaderStyle}
        footerStyle={modalFooterStyle}>
        <ProductListModalContent $hasResult={hasResult}>
          {!isEmpty(searchTermChips) && (
            <SearchFilterChips>
              {searchTermChips.map(([chipSearchType, searchTerm]) => (
                <Chip
                  key={chipSearchType}
                  label={searchTerm}
                  chipBackgroundColor={designSystemTheme.palette.info.light}
                  textColor={designSystemTheme.palette.info.dark}
                  endIcon={
                    <CloseRoundedIcon
                      aria-label={`Remove ${searchTerm}`}
                      sx={{ fontSize: 16 }}
                    />
                  }
                  onEndIconClick={(event) => {
                    event.stopPropagation();
                    handleRemoveSearchTerm(chipSearchType);
                  }}
                />
              ))}
            </SearchFilterChips>
          )}

          <ProductTable>
            <ProductTableHead>
              <ProductTableHeadingText variant="h6">
                {lang.name}
              </ProductTableHeadingText>
              <ProductTableHeadingText variant="h6">
                {lang.variants}
              </ProductTableHeadingText>
            </ProductTableHead>

            {isLoadingProducts ? (
              <LoadingWrapper>
                <CircularProgress data-testid="product-list-modal-loading-progress" />
              </LoadingWrapper>
            ) : !hasResult ? (
              <NoResultsWrapper>
                <NoResultAlert />
              </NoResultsWrapper>
            ) : (
              <ProductRows>{allProducts.map(renderProductRow)}</ProductRows>
            )}
          </ProductTable>

          {hasResult && (
            <Pagination
              page={page}
              totalPages={totalPages}
              loading={isLoading}
              onChange={handlePageChange}
              padding={'16px 16px 0 16px'}
            />
          )}
        </ProductListModalContent>
      </Modal>
    </DesignSystemThemeProvider>
  );
};
